// Cookie consent storage utility (1st-party cookie, 1-year retention)

export type ConsentCategory = 'essential' | 'functional' | 'analytics' | 'marketing';

export interface ConsentPrefs {
  v: 1;
  essential: true; // always true
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  ts: string; // ISO timestamp
}

const COOKIE_NAME = 'lr_consent';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
export const CONSENT_CHANGE_EVENT = 'lr-consent-change';
export const OPEN_SETTINGS_EVENT = 'lr-open-cookie-settings';

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === 'undefined') return;
  const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secure = isHttps ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure}`;
}

export function getConsent(): ConsentPrefs | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.v === 1) {
      return {
        v: 1,
        essential: true,
        functional: !!parsed.functional,
        analytics: !!parsed.analytics,
        marketing: !!parsed.marketing,
        ts: typeof parsed.ts === 'string' ? parsed.ts : new Date().toISOString(),
      };
    }
  } catch {
    // ignore malformed cookie
  }
  return null;
}

export function hasDecided(): boolean {
  return getConsent() !== null;
}

export function setConsent(prefs: Omit<ConsentPrefs, 'v' | 'essential' | 'ts'>) {
  const value: ConsentPrefs = {
    v: 1,
    essential: true,
    functional: !!prefs.functional,
    analytics: !!prefs.analytics,
    marketing: !!prefs.marketing,
    ts: new Date().toISOString(),
  };
  writeCookie(COOKIE_NAME, JSON.stringify(value), ONE_YEAR_SECONDS);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: value }));
  }
  return value;
}

export function acceptAll() {
  return setConsent({ functional: true, analytics: true, marketing: true });
}

export function rejectAll() {
  return setConsent({ functional: false, analytics: false, marketing: false });
}

export function clearConsent() {
  writeCookie(COOKIE_NAME, '', 0);
}

export function openCookieSettings() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}
